import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/Login";
import { DashboardPage } from "./pages/Dashboard";
import { ProtocolsPage } from "./pages/Protocols";
import { ProtocolFormPage } from "./pages/ProtocolForm";
import { ProtocolDetailPage } from "./pages/ProtocolDetail";
import { DocumentTypesPage } from "./pages/DocumentTypes";
import { UsersPage } from "./pages/Users";
import { AuditLogPage } from "./pages/AuditLog";
import { ImportPage } from "./pages/Import";
import { EmailAccountsPage } from "./pages/EmailAccounts";
import { EmailQueuePage } from "./pages/EmailQueue";
import { BackupPage } from "./pages/Backup";
import { ContactsPage } from "./pages/Contacts";
import { ContactProtocolsPage } from "./pages/ContactProtocols";
import { LocationsPage } from "./pages/Locations";
import { OneDriveSettingsPage } from "./pages/OneDriveSettings";
import "./App.css";

// Protected Route wrapper
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-[#2D7BB7]">
          <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
          </svg>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route wrapper (redirects to home if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-[#2D7BB7]">
          <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
          </svg>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/protocols"
        element={
          <ProtectedRoute>
            <ProtocolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/protocols/new"
        element={
          <ProtectedRoute>
            <ProtocolFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/protocols/:id"
        element={
          <ProtectedRoute>
            <ProtocolDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/protocols/:id/edit"
        element={
          <ProtectedRoute>
            <ProtocolFormPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Only Routes */}
      <Route
        path="/settings/types"
        element={
          <ProtectedRoute adminOnly>
            <DocumentTypesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/audit"
        element={
          <ProtectedRoute adminOnly>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/import"
        element={
          <ProtectedRoute adminOnly>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/email-accounts"
        element={
          <ProtectedRoute adminOnly>
            <EmailAccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-queue"
        element={
          <ProtectedRoute>
            <EmailQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/backup"
        element={
          <ProtectedRoute adminOnly>
            <BackupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/contacts"
        element={
          <ProtectedRoute>
            <ContactsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/contacts/:id/protocols"
        element={
          <ProtectedRoute>
            <ContactProtocolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/locations"
        element={
          <ProtectedRoute>
            <LocationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/onedrive"
        element={
          <ProtectedRoute adminOnly>
            <OneDriveSettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
